import { z } from "zod/v4-mini";

import { cz, Schema } from "@/utils/zodUtils";

export type MedicalDevice = {
  id: string;
  serialNumber?: string;
  deviceType: MedicalDeviceType;
  lastUpload?: string;
  lastUploadInfo: unknown;
};

export type MedicalDeviceType = {
  name: string;
  manufacturer: string;
  kind: MedicalDeviceKind;
};

export type MedicalDeviceKind = "pump" | "pen" | "reader" | "sensor" | "manual";

export const medicalDeviceSchema: Schema<MedicalDevice> = cz.transform(
  z.object({
    id: z.coerce.string(),
    serial_number: cz.optional(z.string()),
    device_type: z.object({
      name: z.string(),
      manufacturer: z.string(),
      kind: z.enum(["pump", "pen", "reader", "sensor", "manual"]),
    }),
    last_upload: cz.optional(z.string()),
    last_upload_info: cz.optional(z.unknown()),
  }),
  data => ({
    id: data.id,
    serialNumber: data.serial_number,
    deviceType: {
      name: data.device_type.name,
      manufacturer: data.device_type.manufacturer,
      kind: data.device_type.kind,
    },
    lastUpload: data.last_upload,
    lastUploadInfo: data.last_upload_info ?? undefined,
  }),
);

export type InsulinPump = {
  device: string;
  deviceDate: string;
  deviceTimezone: string;
  profiles: InsulinPumpProfile[];
};

export type InsulinPumpProfile = {
  id: string;
  name: string;
  activeInsulinTime: number;
  segments: InsulinPumpSegment[];
  currentProfile: boolean;
};

export type InsulinPumpSegment = {
  id: string;
  startTime: string;
  endTime: string;
  basalRate: number;
  insulinToCarbRatio: number;
  sensitivity: number;
  bloodGlucoseThreshold: number;
  targetBloodGlucose: number;
};

export type MedicalDeviceCreationDTO = {
  serial_number: string;
  device_type_name: string;
};

export type MedicalDeviceDTO = {
  display_name: string;
  serial_number: string;
  device_type_name: string;
  last_upload?: string;
  last_upload_info?: unknown;
  pump_data?: InsulinPumpDTO;
  // Legacy field, to be removed in the next version (> 1.1.10)
  // API will need to be updated to remove pumps_viewsets > data["pump_data"]["closed_loop_support"] = data["closed_loop_support"]
  closed_loop_support?: "unavailable" | "unsupported_by_steto" | "available";
};

export type InsulinPumpSegmentDTO = {
  start_time: string;
  end_time: string;
  basal_rate: number;
  insulin_to_carb_ratio: number;
  sensitivity: number;
  blood_glucose_threshold?: number;
  target_blood_glucose?: number;
};

export type InsulinPumpProfileDTO = {
  id: string;
  name: string;
  active_insulin_time: number;
  current_profile: boolean;
  basal_insulin_per_day: number;
  segments: InsulinPumpSegmentDTO[];
};

export type InsulinPumpDTO = {
  firmware_version: string;
  profiles: InsulinPumpProfileDTO[];
  closed_loop_support: "unavailable" | "unsupported_by_steto" | "available";
};

export type ClosedLoopDTO =
  | {
      closed_loop_support: "unavailable" | "unsupported_by_steto";
    }
  | {
      closed_loop_support: "available";
      closed_loop_enabled: boolean;
      weight: number;
      sleep_schedules: {
        start: string;
        end: string;
        days: string[];
      }[];
      total_daily_insulin: number;
    };
